import { type FC } from "react";

import { useTeamsChatSdkEnabled } from "../../../../hooks/useTeamsChatSdkEnabled";
import {
  type BlackboardInstance,
  type WakeBlockBadgeCopy,
  wakeBlockBadgeLabel,
} from "../../blackboardModels";
import { ChannelManager } from "../../identity/ChannelManager";
import { IdentityPromptSection } from "../../identity/IdentityPromptSection";
import { InfoRow } from "../../identity/InfoRow";
import { OverrideControls } from "../../identity/OverrideControls";
import { ProvenanceSection } from "../../identity/ProvenanceSection";
import { RuntimeSection } from "../../identity/RuntimeSection";
import { StatusSection } from "../../identity/StatusSection";
import { TeamsInviteSection } from "../../identity/TeamsInviteSection";

// Re-export the section + type exports that used to live in this file so
// existing imports (e.g. __tests__/InfoTab.test.ts) keep resolving after the
// decomposition. New code should import from ../../identity/* directly.
export { WakeBlockedDetailsBlock } from "../../identity/WakeBlockedDetailsBlock";
export type { TeamsBindLink } from "../../identity/helpers";
export { TeamsBindLinkPanel } from "../../identity/TeamsBindLinkPanel";

// ---------------------------------------------------------------------------
// InfoTab — actor identity overview.
//
// Thin composer over the single-responsibility sections under ../../identity/.
// The previous version was a 1340-line god component holding every inlined
// helper + section; this keeps the same public export + props so callers
// (InspectorSheet actor mode) need no changes. Behavior is identical; only
// the code organization changed.
// ---------------------------------------------------------------------------

interface InfoTabProps {
  instance: BlackboardInstance;
  onKill: () => void;
  killing: boolean;
  onChannelsChange?: () => void;
  /** Called after any instance field save (identity prompt, overrides, model,
   *  cooldown) so the parent refetches and props stop being stale. */
  onInstanceChange?: () => void;
  onOpenChannelWorkspace?: (channelId: string) => void;
}

export const InfoTab: FC<InfoTabProps> = ({
  instance,
  onKill,
  killing,
  onChannelsChange,
  onInstanceChange,
  onOpenChannelWorkspace,
}) => {
  const teamsChatSdk = useTeamsChatSdkEnabled();
  const wakeBlockDetails: WakeBlockBadgeCopy | null =
    wakeBlockBadgeLabel(instance);

  // Co-locate the Channels row with its section (the ChannelManager owns the
  // Teams bind/unbind lifecycle, and the per-channel Workspace pivot lives on
  // each tag). Keeping this as a local section avoids re-introducing a god
  // component while preserving the exact join/leave/bind affordances.
  const ChannelsSection = (
    <InfoRow label="Channels">
      <ChannelManager
        instanceId={instance.id}
        channels={instance.channels}
        onChannelsChange={onChannelsChange}
        onOpenChannelWorkspace={onOpenChannelWorkspace}
        teamsChatSdkEnabled={teamsChatSdk.enabled}
      />
    </InfoRow>
  );

  return (
    <div className="divide-y divide-emerald-400/5">
      <StatusSection instance={instance} wakeBlockDetails={wakeBlockDetails} />
      <ProvenanceSection instance={instance} />
      <RuntimeSection instance={instance} />
      {ChannelsSection}
      <TeamsInviteSection
        instance={instance}
        teamsChatSdkEnabled={teamsChatSdk.enabled}
      />
      {instance.status !== "killed" && instance.overrides && (
        <OverrideControls
          instanceId={instance.id}
          overrides={instance.overrides}
          modelRef={instance.modelRef}
          tickIntervalMs={instance.tickIntervalMs}
          onSaved={onInstanceChange}
        />
      )}
      <IdentityPromptSection
        instance={instance}
        onKill={onKill}
        killing={killing}
        onSaved={onInstanceChange}
      />
    </div>
  );
};
